'use client';

/**
 * LeadsTable
 *
 * Renders the structured payload returned by `crm_list_leads`.
 * Issue #1432 Phase 2 — Wave 3 P2
 */

import Box from '@mui/material/Box';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { DataTableColumn } from '../../types/dataTable';
import { DataTable } from '../DataTable/DataTable';
import { GlassChip } from '../GlassChip';
import { RendererEmptyState } from './RendererEmptyState';
import { formatStatusLabel, getStatusColor } from './statusColor';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema
// ============================================================================

const LeadSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  source: z.string().optional(),
  status: z.string().optional(),
  score: z.number().optional(),
  createdAt: z.string().optional(),
  assignedTo: z.string().optional(),
});

type LeadRow = z.infer<typeof LeadSchema>;

const LeadsDataSchema = z.object({
  leads: z.array(LeadSchema),
  total: z.number().optional(),
});

const LeadsArraySchema = z.array(LeadSchema);

function parseLeadsData(data: unknown): LeadRow[] | null {
  const wrapped = LeadsDataSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.leads;

  const bareArray = LeadsArraySchema.safeParse(data);
  if (bareArray.success) return bareArray.data;

  return null;
}

// ============================================================================
// Component
// ============================================================================

export const LeadsTable = ({ data, onNavigate }: AstorToolRendererProps): ReactElement => {
  const { t, formatNumber, formatDate } = useI18n();
  const rows = useMemo(() => parseLeadsData(data), [data]);

  const columns: DataTableColumn<LeadRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('astorTools.leads.name'),
        accessor: 'name',
      },
      {
        id: 'source',
        header: t('astorTools.leads.source'),
        accessor: 'source',
      },
      {
        id: 'status',
        header: t('astorTools.leads.status'),
        accessor: 'status',
        sortable: true,
        cell: (value) => {
          const status = typeof value === 'string' ? value : '';
          if (!status) return '';
          return (
            <GlassChip
              label={formatStatusLabel(status)}
              color={getStatusColor(status)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        id: 'score',
        header: t('astorTools.leads.score'),
        accessor: 'score',
        align: 'right',
        sortable: true,
        cell: (value) => (typeof value === 'number' ? formatNumber(value) : ''),
      },
      {
        id: 'createdAt',
        header: t('astorTools.leads.createdAt'),
        accessor: 'createdAt',
        sortable: true,
        cell: (value) => {
          if (typeof value !== 'string' || !value) return '';
          try {
            return formatDate(value, 'medium');
          } catch {
            return value;
          }
        },
      },
      {
        id: 'assignedTo',
        header: t('astorTools.leads.assignedTo'),
        accessor: 'assignedTo',
      },
    ],
    [t, formatNumber, formatDate],
  );

  if (rows === null) {
    return (
      <RendererEmptyState
        title={t('astorTools.error.parseFailed')}
        description={t('astorTools.error.parseFailedDescription')}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <RendererEmptyState title={t('astorTools.leads.empty')} description={t('astorTools.leads.emptyDescription')} />
    );
  }

  return (
    <Box sx={{ my: 1.5 }}>
      <DataTable<LeadRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        sortable
        paginated
        pagination={{ page: 0, pageSize: 10, pageSizeOptions: [10, 25, 50] }}
        density="compact"
        variant="glass"
        onRowClick={onNavigate ? (row) => onNavigate(`/leads/${row.id}`) : undefined}
      />
    </Box>
  );
};

export default LeadsTable;
